/* eslint-disable angular/log */
/**
 * Dev-only debug helper for inspecting AngularJS component controllers from the console.
 *
 * API:
 *   console.get('summary-page')         — get $ctrl by selector
 *   console.last()                      — last accessed $ctrl
 *   console.list('discount')            — filter component names
 *   console.list('discount').props()    — props of first match
 *   console.list('discount').get()      — $ctrl of first match
 *   console.props('summary-page')       — table of scalars + expandable objects/arrays
 *   console.props('summary-page', 'resolve') — deep snapshot of a single prop
 */
export function installDbg() {
	console.get = selector => {
		const el = document.querySelector(selector);
		if (!el) return console.warn(`get: element "${selector}" not found`);
		const $el = angular.element(el);
		const scope = $el.isolateScope() || $el.scope();
		if (!scope) return console.warn(`get: no scope found on "${selector}"`);
		console._last = scope.$ctrl || scope;
		return console._last;
	};

	console.last = () => console._last;

	// Array subclass so props()/get() survive chaining (filter/map/slice
	// build new instances via Symbol.species, keeping the prototype helpers).
	class DbgList extends Array {
		props(index = 0) {
			if (!this[index]) return console.warn('list: no match at index', index);
			return console.props(this[index]);
		}
		get(index = 0) {
			if (!this[index]) return console.warn('list: no match at index', index);
			return console.get(this[index]);
		}
	}

	console.list = filter => {
		const all = document.querySelectorAll('[class],[ng-scope]');
		const components = new Set();
		all.forEach(el => {
			const scope = angular.element(el).isolateScope();
			if (scope?.$ctrl) components.add(el.tagName.toLowerCase());
		});
		const sorted = DbgList.from(components).sort();
		return filter ? sorted.filter(name => name.includes(filter)) : sorted;
	};

	console.props = (selector, key) => {
		const ctrl = console.get(selector);
		if (!ctrl) return;

		// Deep-snapshot a single prop: console.props('add-product-modal', 'resolve')
		// structuredClone is polyfilled globally; falls back to the live ref if it can't clone (e.g. functions).
		if (key) {
			if (!(key in ctrl)) return console.warn(`props: "${key}" not found on ${selector}`);
			let snapshot;
			try {
				snapshot = structuredClone(ctrl[key]);
			} catch {
				snapshot = ctrl[key];
			}
			console.dir(snapshot);
			return snapshot;
		}

		const props = {functions: [], values: {}};
		Object.keys(ctrl).forEach(k => {
			if (k.startsWith('$') || k.startsWith('_')) return;
			if (typeof ctrl[k] === 'function') props.functions.push(k);
			else props.values[k] = ctrl[k];
		});
		props.functions.sort();

		// Split scalars from objects/arrays.
		const scalars = {};
		const objects = {};
		Object.entries(props.values).forEach(([k, v]) => {
			if (v !== null && typeof v === 'object') objects[k] = v;
			else scalars[k] = v;
		});

		// Scalars: a compact table is useful and fully readable.
		if (Object.keys(scalars).length) {
			console.table(Object.entries(scalars).map(([k, v]) => ({key: k, type: typeof v, value: v})));
		}

		// Objects/arrays: log the REAL value directly so the console renders it as an
		// inline, fully-expandable tree (console.table / {…} placeholders can't do this).
		Object.entries(objects).forEach(([k, v]) => {
			console.log(
				`%c${k}%c (${Array.isArray(v) ? `array[${v.length}]` : 'object'}):`,
				'font-weight:bold',
				'',
				v
			);
		});

		console.log('functions:', props.functions.join(', '));
		console.log(`tip: console.props('${selector}', 'propName') for a deep snapshot of one prop`);
		return props;
	};
}
